import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@heyroot/ui/primitives/Button';
import { Checkbox } from '@heyroot/ui/primitives/Checkbox';
import { Input } from '@heyroot/ui/primitives/Input';
import { Select } from '@heyroot/ui/primitives/Select';
import {
  createBusinessType,
  fetchBusinessTypeCategories,
  queryKeys,
  AdminApiError,
} from '../lib/api';

// Server requires /^[a-z][a-z0-9_]*$/ (modules/tenants/server/admin-business-types-routes.ts).
const SLUG_PATTERN = /^[a-z][a-z0-9_]*$/;
// Mirrors CreateBody in modules/tenants/server/admin-business-types-routes.ts:
// slug is .max(64) and sortOrder is .int().min(0).max(9999). Kept in step with
// those bounds so an out-of-range value is named against its own field instead
// of returning the generic 400 this form exists to avoid.
const SLUG_MAX = 64;
const SORT_ORDER_MAX = 9999;
const SLUG_PATTERN_MESSAGE =
  'must start with a lowercase letter and use only lowercase letters, digits and underscores';

function slugProblem(value: string): string | null {
  if (value.length === 0) return null;
  if (!SLUG_PATTERN.test(value)) return `Slug ${SLUG_PATTERN_MESSAGE}.`;
  if (value.length > SLUG_MAX) return `Slug must be ${SLUG_MAX} characters or fewer.`;
  return null;
}

/** `Full Service Restaurant` -> `full_service_restaurant`; strips anything
 * that isn't a lowercase letter, digit or underscore so a pasted name never
 * produces an invalid slug.
 *
 * A leading digit is prefixed rather than stripped: stripping turned
 * `7-Eleven` into `eleven` and an all-digit name into an empty slug, both
 * silently and both wrong. The server requires a leading letter, so `t_` is
 * added and the operator can see and edit the result. */
function slugify(name: string): string {
  const base = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (base.length === 0) return '';
  const prefixed = /^[0-9]/.test(base) ? `t_${base}` : base;
  return prefixed.slice(0, SLUG_MAX);
}

export function BusinessTypeNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: cats } = useQuery({
    queryKey: queryKeys.businessTypeCategories,
    queryFn: fetchBusinessTypeCategories,
  });

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [showAtSignup, setShowAtSignup] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createBusinessType({
        name,
        slug,
        categoryId: categoryId || null,
        description: description || null,
        showAtSignup,
        sortOrder,
      }),
    onSuccess: async () => {
      setSlugError(null);
      setFormError(null);
      await qc.invalidateQueries({ queryKey: ['business-types'] });
      void navigate({ to: '/business-types/$businessType', params: { businessType: slug } });
    },
    onError: (err) => {
      if (err instanceof AdminApiError && err.code === 'SLUG_TAKEN') {
        setSlugError('That slug is already in use.');
        return;
      }
      setFormError(err instanceof Error ? err.message : 'Could not create business type.');
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/business-types"
        className="inline-flex items-center gap-1 text-body-sm text-secondary hover:text-primary"
      >
        <ChevronLeft size={14} /> Business Types
      </Link>

      <div>
        <h1 className="text-heading-2 text-primary">New business type</h1>
        <p className="mt-1 text-body-sm text-muted">
          Modules, role templates and accounting defaults are set on its first draft version after
          it's created.
        </p>
      </div>

      <form
        className="flex max-w-xl flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        {formError && (
          <div
            role="alert"
            className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-3"
          >
            {formError}
          </div>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Name</span>
          <Input
            required
            value={name}
            onChange={(e) => {
              const v = e.target.value;
              setName(v);
              if (!slugEdited) setSlug(slugify(v));
            }}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Slug</span>
          <Input
            required
            value={slug}
            invalid={Boolean(slugError) || slugProblem(slug) !== null}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(e.target.value);
              setSlugError(null);
            }}
          />
          {slugError ? (
            <span className="text-body-sm text-danger-text">{slugError}</span>
          ) : slugProblem(slug) !== null ? (
            <span className="text-body-sm text-danger-text">{slugProblem(slug)}</span>
          ) : (
            <span className="text-body-sm text-muted">Cannot be changed once created.</span>
          )}
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Category</span>
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">No category</option>
            {(cats?.rows ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Description</span>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label className="flex items-center gap-2">
          <Checkbox checked={showAtSignup} onChange={(e) => setShowAtSignup(e.target.checked)} />
          <span className="text-body-sm text-secondary">Offer at signup</span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Sort order</span>
          <Input
            type="number"
            min={0}
            max={SORT_ORDER_MAX}
            step={1}
            value={sortOrder}
            onChange={(e) =>
              // Clamp BOTH bounds and round: the server takes .int().min(0).max(9999),
              // so 12.5 and 50000 were both reaching it and 400ing generically.
              setSortOrder(
                Math.min(SORT_ORDER_MAX, Math.max(0, Math.round(Number(e.target.value) || 0))),
              )
            }
            className="max-w-32"
          />
        </label>

        <div className="mt-2 flex gap-2">
          <Button
            type="submit"
            loading={create.isPending}
            disabled={!name || !slug || !SLUG_PATTERN.test(slug)}
          >
            Create
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigate({ to: '/business-types' })}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
